import React, { useState, useEffect } from 'react';
import { 
  Typography, 
  Box, 
  Paper, 
  Grid,
  Button,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Snackbar,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useNavigate, useParams } from 'react-router-dom';
import Alert from '@material-ui/lab/Alert';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import { firestore } from '../../firebase/config';
import { doc, getDoc, deleteDoc } from 'firebase/firestore';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
  },
  paper: {
    width: '100%',
    padding: theme.spacing(3),
  },
  section: {
    marginBottom: theme.spacing(3),
  },
  divider: {
    margin: theme.spacing(2, 0),
  },
  buttons: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: theme.spacing(3),
  },
  button: {
    marginLeft: theme.spacing(1),
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(3),
  },
  chip: {
    margin: theme.spacing(0.5),
  },
  infoGrid: {
    marginBottom: theme.spacing(2),
  },
  infoLabel: {
    fontWeight: 'bold',
  },
  tableContainer: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  title: {
    marginBottom: theme.spacing(2),
  },
  deleteButton: {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.error.dark,
    },
  },
}));

const CouponDetailPage = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const { id } = useParams();
  const [coupon, setCoupon] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  useEffect(() => {
    fetchCoupon();
  }, [id]);

  const fetchCoupon = async () => {
    try {
      // Simulation de récupération depuis Firestore
      // Dans une implémentation réelle, cela serait remplacé par un appel à l'API
      const couponDoc = doc(firestore, 'coupons', id);
      const couponSnapshot = await getDoc(couponDoc);
      
      if (couponSnapshot.exists()) {
        setCoupon({
          id: couponSnapshot.id,
          ...couponSnapshot.data()
        });
      } else {
        // Si le coupon n'existe pas, utiliser des données de démonstration
        if (id === 'WELCOME10') {
          setCoupon({ 
            id: 'WELCOME10', 
            code: 'WELCOME10',
            description: 'Coupon de bienvenue pour les nouveaux utilisateurs',
            discountType: 'percentage',
            discountValue: 10,
            minOrderValue: 0,
            maxDiscountAmount: 50,
            usageLimit: 1,
            usageCount: 45,
            startDate: '2025-01-01T00:00:00',
            endDate: '2025-12-31T23:59:59',
            status: 'active',
            createdAt: '2025-01-01T10:00:00',
            updatedAt: '2025-01-01T10:00:00',
            usageHistory: [
              {
                userId: 'U001',
                userName: 'Alice Martin',
                bookingId: 'B001',
                usedAt: '2025-03-20T14:30:00',
                discountAmount: 10.50
              },
              {
                userId: 'U002',
                userName: 'Thomas Bernard',
                bookingId: 'B002',
                usedAt: '2025-03-21T10:15:00',
                discountAmount: 4.60
              }
            ]
          });
        } else {
          setSnackbar({
            open: true,
            message: 'Coupon non trouvé',
            severity: 'error'
          });
        }
      }
    } catch (error) {
      console.error('Error fetching coupon:', error);
      setSnackbar({
        open: true,
        message: 'Erreur lors de la récupération des données du coupon',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/admin/coupons');
  };

  const handleEdit = () => {
    navigate(`/admin/coupons/${id}/edit`);
  };

  const handleDeleteClick = () => {
    setOpenDeleteDialog(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      // Simulation de suppression dans Firestore
      // Dans une implémentation réelle, cela serait remplacé par un appel à l'API
      await deleteDoc(doc(firestore, 'coupons', id));
      
      setSnackbar({
        open: true,
        message: 'Coupon supprimé avec succès',
        severity: 'success'
      });
      
      // Rediriger vers la liste des coupons après un court délai
      setTimeout(() => {
        navigate('/admin/coupons');
      }, 1500);
      
    } catch (error) {
      console.error('Error deleting coupon:', error);
      setSnackbar({
        open: true,
        message: 'Erreur lors de la suppression du coupon',
        severity: 'error'
      });
    } finally {
      setOpenDeleteDialog(false);
    }
  };

  const handleDeleteCancel = () => {
    setOpenDeleteDialog(false);
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'primary';
      case 'expired':
        return 'default';
      case 'disabled':
        return 'secondary';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'active':
        return 'Actif';
      case 'expired':
        return 'Expiré';
      case 'disabled':
        return 'Désactivé';
      default:
        return status;
    }
  };

  const formatDate = (dateString) => {
    const options = { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric', 
      hour: '2-digit', 
      minute: '2-digit' 
    };
    return new Date(dateString).toLocaleDateString('fr-FR', options);
  };

  const formatDiscount = (type, value) => {
    if (type === 'percentage') {
      return `${value}%`;
    } else if (type === 'fixed') {
      return `${value}€`;
    }
    return value;
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(price);
  };

  return (
    <Box className={classes.root}>
      <Button
        variant="outlined"
        color="primary"
        startIcon={<ArrowBackIcon />}
        onClick={handleBack}
        style={{ marginBottom: 16 }}
      >
        Retour à la liste
      </Button>
      
      <Typography variant="h4" component="h1" gutterBottom>
        Détails du coupon
      </Typography>
      
      <Paper className={classes.paper}>
        {loading ? (
          <div className={classes.loading}>
            <CircularProgress />
          </div>
        ) : coupon ? (
          <>
            <Box className={classes.section}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={8}>
                  <Typography variant="h5" component="h2">
                    {coupon.code}
                  </Typography>
                  <Typography variant="subtitle1" color="textSecondary">
                    {coupon.description}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4} style={{ textAlign: 'right' }}>
                  <Chip 
                    label={getStatusLabel(coupon.status)} 
                    color={getStatusColor(coupon.status)} 
                    className={classes.chip}
                  />
                </Grid>
              </Grid>
            </Box>
            
            <Divider className={classes.divider} />
            
            <Box className={classes.section}>
              <Typography variant="h6" gutterBottom>
                Informations générales
              </Typography>
              
              <Grid container spacing={2} className={classes.infoGrid}>
                <Grid item xs={12} sm={4}>
                  <Typography variant="body2" className={classes.infoLabel}>
                    Type de réduction
                  </Typography>
                  <Typography variant="body1">
                    {coupon.discountType === 'percentage' ? 'Pourcentage' : 'Montant fixe'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="body2" className={classes.infoLabel}>
                    Valeur de la réduction
                  </Typography>
                  <Typography variant="body1">
                    {formatDiscount(coupon.discountType, coupon.discountValue)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="body2" className={classes.infoLabel}>
                    Montant maximum de réduction
                  </Typography>
                  <Typography variant="body1">
                    {coupon.maxDiscountAmount > 0 ? formatPrice(coupon.maxDiscountAmount) : 'Pas de maximum'}
                  </Typography>
                </Grid>
              </Grid>
              
              <Grid container spacing={2} className={classes.infoGrid}>
                <Grid item xs={12} sm={4}>
                  <Typography variant="body2" className={classes.infoLabel}>
                    Valeur minimale de commande
                  </Typography>
                  <Typography variant="body1">
                    {coupon.minOrderValue > 0 ? formatPrice(coupon.minOrderValue) : 'Pas de minimum'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="body2" className={classes.infoLabel}>
                    Limite d'utilisation
                  </Typography>
                  <Typography variant="body1">
                    {coupon.usageLimit > 0 ? `${coupon.usageCount} / ${coupon.usageLimit}` : `${coupon.usageCount} / Illimité`}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="body2" className={classes.infoLabel}>
                    Période de validité
                  </Typography>
                  <Typography variant="body1">
                    {formatDate(coupon.startDate).split(' ')[0]} - {formatDate(coupon.endDate).split(' ')[0]}
                  </Typography>
                </Grid>
              </Grid>
              
              <Grid container spacing={2} className={classes.infoGrid}>
                <Grid item xs={12} sm={4}>
                  <Typography variant="body2" className={classes.infoLabel}>
                    Date de création
                  </Typography>
                  <Typography variant="body1">
                    {formatDate(coupon.createdAt)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="body2" className={classes.infoLabel}>
                    Dernière modification
                  </Typography>
                  <Typography variant="body1">
                    {formatDate(coupon.updatedAt)}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
            
            {coupon.usageHistory && coupon.usageHistory.length > 0 && (
              <>
                <Divider className={classes.divider} />
                
                <Box className={classes.section}>
                  <Typography variant="h6" gutterBottom className={classes.title}>
                    Historique d'utilisation
                  </Typography>
                  
                  <TableContainer component={Paper} className={classes.tableContainer}>
                    <Table aria-label="usage history table">
                      <TableHead>
                        <TableRow>
                          <TableCell>Utilisateur</TableCell>
                          <TableCell>Réservation</TableCell>
                          <TableCell>Date d'utilisation</TableCell>
                          <TableCell align="right">Montant réduit</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {coupon.usageHistory.map((usage, index) => (
                          <TableRow key={index}>
                            <TableCell>{usage.userName}</TableCell>
                            <TableCell>{usage.bookingId}</TableCell>
                            <TableCell>{formatDate(usage.usedAt)}</TableCell>
                            <TableCell align="right">{formatPrice(usage.discountAmount)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              </>
            )}
            
            <Divider className={classes.divider} />
            
            <div className={classes.buttons}>
              <Button
                variant="outlined"
                color="primary"
                onClick={handleBack}
                className={classes.button}
              >
                Retour
              </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={<EditIcon />}
                onClick={handleEdit}
                className={classes.button}
              >
                Modifier
              </Button>
              <Button
                variant="contained"
                startIcon={<DeleteIcon />}
                onClick={handleDeleteClick}
                className={`${classes.button} ${classes.deleteButton}`}
              >
                Supprimer
              </Button>
            </div>
          </>
        ) : (
          <Typography variant="body1">
            Coupon non trouvé
          </Typography>
        )}
      </Paper>
      
      {/* Dialog de confirmation de suppression */}
      <Dialog
        open={openDeleteDialog}
        onClose={handleDeleteCancel}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{"Confirmer la suppression"}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Êtes-vous sûr de vouloir supprimer définitivement le coupon <strong>{coupon?.code}</strong> ? Cette action est irréversible.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary">
            Annuler
          </Button>
          <Button onClick={handleDeleteConfirm} color="secondary" autoFocus>
            Supprimer
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Snackbar pour les notifications */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CouponDetailPage;
